import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { scrollToId } from "./SmoothScroll";

const ease = [0.22, 1, 0.36, 1];
const lines = [
  { t: "As cartas", c: "" },
  { t: "falam pelas", c: "italic gold-text pl-[8vw] md:pl-24" },
  { t: "mãos.", c: "" },
];

const MaskLine = ({ children, i, className }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span className={`block ${className}`} initial={{ y: "110%" }} animate={{ y: 0 }}
      transition={{ duration: 1.2, delay: 0.35 + i * 0.14, ease }}>
      {children}
    </motion.span>
  </span>
);

const TiltCard = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 120, damping: 18 });
  const move = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const leave = () => { mx.set(0); my.set(0); };
  return (
    <div className="[perspective:1200px]" onMouseMove={move} onMouseLeave={leave}>
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        className="relative mx-auto aspect-[2/3] w-[62vw] max-w-[340px] sm:w-[300px]">
        <div className="arch-round absolute inset-0 overflow-hidden border border-[#CBA365]/50 bg-[#1A0A2E] shadow-[0_40px_120px_-20px_rgba(109,40,217,0.55)]">
          <img src="/img/hero.jpg" alt="Cartas de tarot à luz de vela" className="h-full w-full object-cover opacity-90" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#080312] via-transparent to-transparent" />
        </div>
        <div className="arch-round pointer-events-none absolute inset-3 border border-[#CBA365]/40" style={{ transform: "translateZ(40px)" }} />
        <div className="absolute inset-x-0 bottom-6 text-center" style={{ transform: "translateZ(60px)" }}>
          <p className="font-display text-sm uppercase tracking-[0.4em] text-[var(--gold)]">XVII</p>
          <p className="font-display text-2xl italic text-[#F5F2EB]">A Estrela</p>
        </div>
      </motion.div>
    </div>
  );
};

export const Hero = ({ onBook }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yCard = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const rot = useTransform(scrollYProgress, [0, 1], [0, 90]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-[100svh] overflow-hidden pt-28 md:pt-32">
      <img src="/img/texture.jpg" alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover opacity-[0.18] mix-blend-screen" />
      <div className="absolute -left-40 top-1/3 h-[520px] w-[520px] rounded-full bg-[#6D28D9]/30 blur-[140px]" />
      <motion.svg style={{ rotate: rot }} viewBox="0 0 400 400" className="spin-slow pointer-events-none absolute -right-40 -top-24 h-[620px] w-[620px] text-[#CBA365]/10 md:text-[#CBA365]/20 md:right-[-120px]">
        <circle cx="200" cy="200" r="190" fill="none" stroke="currentColor" />
        <circle cx="200" cy="200" r="150" fill="none" stroke="currentColor" strokeDasharray="2 8" />
        {Array.from({ length: 12 }).map((_, i) => (
          <line key={i} x1="200" y1="10" x2="200" y2="50" stroke="currentColor" transform={`rotate(${i * 30} 200 200)`} />
        ))}
      </motion.svg>

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 pb-20 md:px-10 lg:grid-cols-12">
        <motion.div style={{ y: yText }} className="lg:col-span-7">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }}
            className="mb-8 text-xs uppercase tracking-[0.35em] text-[var(--gold)]">
            Tarot online · via WhatsApp · Pix
          </motion.p>
          <h1 data-testid="hero-title" className="font-display text-[15vw] font-medium leading-[0.9] tracking-tighter text-[#F5F2EB] sm:text-7xl lg:text-[6.6rem]">
            {lines.map((l, i) => <MaskLine key={l.t} i={i} className={l.c}>{l.t}</MaskLine>)}
          </h1>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 1, ease }}
            className="mt-10 flex max-w-xl flex-col gap-8 sm:flex-row sm:items-end">
            <p className="text-base leading-relaxed text-white/75">
              Leituras de tarot sinceras e acolhedoras, direto no seu WhatsApp. Escolha sua tiragem, pague via Pix e a conversa começa após a confirmação do pagamento. O atendimento tem o prazo de 24 horas para as respostas atendidas. Dependendo de ordens de agendamentos no dia, aguarde ser chamado para obter a(s) resposta(s). Desde já, grato.
            </p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.15, duration: 1, ease }}
            className="mt-10 flex flex-wrap items-center gap-6">
            <button data-testid="hero-book-btn" onClick={() => onBook()}
              className="group inline-flex items-center gap-3 rounded-full bg-[var(--gold)] px-8 py-4 text-sm font-semibold uppercase tracking-[0.18em] text-[#080312] transition-[transform,box-shadow,background-color] duration-300 hover:scale-[1.03] hover:bg-[var(--gold-hi)] hover:shadow-[0_0_40px_rgba(203,163,101,0.45)]">
              Consultar as cartas
              <ArrowDownRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-[-45deg]" />
            </button>
            <button data-testid="hero-prices-btn" onClick={() => scrollToId("leituras")}
              className="text-sm tracking-wide text-white/75 underline decoration-[#CBA365]/50 underline-offset-8 transition-colors duration-300 hover:text-[var(--gold)]">
              Ver valores — a partir de R$ 35
            </button>
          </motion.div>
        </motion.div>

        <motion.div style={{ y: yCard }} initial={{ opacity: 0, scale: 0.92, rotate: -4 }} animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ duration: 1.6, delay: 0.5, ease }} className="relative lg:col-span-5">
          <TiltCard />
        </motion.div>
      </div>
    </section>
  );
};
